import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import { Calculate, Info } from '@mui/icons-material';
import { MathFunctions } from '../utils/MathFunctions';
import ResponsiveDiagram from './ResponsiveDiagram';

const FlareRad: React.FC = () => {
  const [roughCut, setRoughCut] = useState('');
  const [flareAmount, setFlareAmount] = useState('');
  const [numFlare, setNumFlare] = useState('');
  const [flareRadius, setFlareRadius] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [calculatedInputs, setCalculatedInputs] = useState<string | null>(null);
  const currentInputs = JSON.stringify([roughCut, flareAmount, numFlare]);
  const isStale = calculatedInputs !== null && calculatedInputs !== currentInputs;

  const handleCalculate = () => {
    if (!roughCut || !flareAmount || !numFlare) {
      setShowError(true);
      return;
    }

    const roughCutNum = parseFloat(roughCut);
    const flareAmountNum = parseFloat(flareAmount);
    const numFlareNum = parseFloat(numFlare);

    if (isNaN(roughCutNum) || isNaN(flareAmountNum) || isNaN(numFlareNum)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setCalculatedInputs(currentInputs);

    // FlareRad calculation logic from Android app
    const lengthA = roughCutNum * numFlareNum;
    
    // Convert angle calculation to degrees
    const angleB = Math.atan(flareAmountNum / lengthA) * 180 / Math.PI;
    
    // Calculate hypotenuse
    const lengthC = Math.sqrt((lengthA * lengthA) + (flareAmountNum * flareAmountNum));
    
    // Calculate angles
    const angleD = 90 - angleB;
    const angleE = 180 - angleD - angleD;
    
    // Use MathFunctions to calculate radius (using law of sines)
    const radius = MathFunctions.calcLength(lengthC, angleE, angleD);
    
    // Round to 2 decimal places
    const roundedRadius = Math.round(radius * 100) / 100;

    setFlareRadius(roundedRadius.toString());
  };

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Calculate Flare Radius
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Determine radius for flared stair treads
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Input Form */}
          <Card component="form" noValidate onSubmit={(event) => { event.preventDefault(); handleCalculate(); }} elevation={3} sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Input Values
                </Typography>
              </Box>

              <Stack spacing={3}>
                {showError && (
                  <Alert severity="error">Enter a number in each highlighted field, then calculate again.</Alert>
                )}
                <TextField
                  fullWidth
                  label="Rough Cut (RC)"
                  type="number"
                  value={roughCut}
                  error={showError && (!roughCut || isNaN(parseFloat(roughCut)))}
                  onChange={(e) => setRoughCut(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Width of rough cut tread"
                />
                
                <TextField
                  fullWidth
                  label="Flare Amount"
                  type="number"
                  value={flareAmount}
                  error={showError && (!flareAmount || isNaN(parseFloat(flareAmount)))}
                  onChange={(e) => setFlareAmount(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Total flare distance"
                />

                <TextField
                  fullWidth
                  label="Number of Flared Steps"
                  type="number"
                  value={numFlare}
                  error={showError && (!numFlare || isNaN(parseFloat(numFlare)))}
                  onChange={(e) => setNumFlare(e.target.value)}
                  inputProps={{ step: "1", inputMode: 'numeric' }}
                  helperText="How many steps will be flared"
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  startIcon={<Calculate />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0D47A1 30%, #1976D2 90%)',
                    }
                  }}
                >
                  Calculate
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Results */}
          <Card component="section" aria-label="Calculation results" aria-live="polite" aria-atomic="true" elevation={3} sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Results
                </Typography>
              </Box>

              {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Inputs changed. These results are from your previous calculation. Calculate again to update them.
                </Alert>
              )}

              {flareRadius ? (
                <Stack spacing={2}>
                  <Box>
                    <Chip 
                      label="Flare Radius" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Flare Radius" }}
                      fullWidth
                      value={`${flareRadius} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mt: 2, p: 2, bgcolor: '#edf5fc', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      <strong>Formula:</strong> Uses law of sines with trigonometric calculations
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Calculate sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">
                    Enter all values and click Calculate to see flare radius
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Diagram */}
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Reference Diagram
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveDiagram 
              imageName="flare_rad" 
              alt="Flare radius calculation diagram showing rough cut and flare measurements" 
            />
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
};

export default FlareRad;